/**
 * EditEmailDialog — single-field edit for the user's email + a "remove"
 * affordance because email is nullable in the schema.
 *
 * V1.0.x note (echoed in the form): email is unverified — used for billing
 * and recovery, not the credential. Phone OTP is the only path to a session.
 *
 * Same plain-state pattern as `EditNameDialog`; see that file for the
 * "why no RHF here" rationale.
 */
import * as React from "react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Alert,
} from "@flint/ui";
import { identity as contract } from "@flint/api-contract";
import { trpc } from "../../lib/trpc.ts";

export interface EditEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEmail: string | null;
  onUpdated: () => void;
}

export function EditEmailDialog({
  open,
  onOpenChange,
  initialEmail,
  onUpdated,
}: EditEmailDialogProps) {
  const [email, setEmail] = React.useState(initialEmail ?? "");
  const [clientError, setClientError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setEmail(initialEmail ?? "");
      setClientError(null);
    }
  }, [open, initialEmail]);

  const update = trpc.identity.updateProfile.useMutation({
    onSuccess: () => {
      onUpdated();
      onOpenChange(false);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    // Wire schema accepts '' as "clear to null" — the domain treats them
    // identically. Trim whitespace before validating so a stray space
    // doesn't trip the Zod email regex.
    const trimmed = email.trim();
    const parsed = contract.updateProfileInput.safeParse({ email: trimmed });
    if (!parsed.success) {
      setClientError(parsed.error.issues[0]?.message ?? "Invalid email.");
      return;
    }
    const next = parsed.data.email ?? null;
    const current = initialEmail ?? null;
    // Same "no round-trip when nothing changed" shortcut as the name dialog.
    if ((next ?? null) === current) {
      onOpenChange(false);
      return;
    }
    update.mutate({ email: trimmed });
  };

  const wireError = update.error?.message ?? null;
  const hasEmail = (initialEmail ?? "").length > 0;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Edit email</DialogTitle>
            <DialogDescription>
              Used for billing and account recovery. Phone is still your sign-in credential.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex flex-col gap-2">
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              autoFocus
              type="email"
              autoComplete="email"
              inputMode="email"
              maxLength={255}
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={update.isPending}
            />
            <p className="text-xs text-muted">
              Leave blank to remove. You can verify your email after saving.
            </p>
            {clientError ? <p className="text-xs text-bad">{clientError}</p> : null}
            {wireError && !clientError ? (
              <Alert intent="bad" title="Couldn't save" description={wireError} />
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            {hasEmail ? (
              // Quick-clear so the user doesn't have to manually empty the
              // input. The submit handler treats '' as the clear signal.
              <Button
                type="button"
                intent="ghost"
                onClick={() => setEmail("")}
                disabled={update.isPending}
              >
                Remove
              </Button>
            ) : null}
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
